import { SheetProps } from "@/types/ui";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { router, useForm, usePage } from "@inertiajs/react";
import { Spinner } from "@/components/ui/spinner";
import { FormEvent } from "react";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { account_path } from "@/lib/routes";
import { CalendarIcon, CreditCardIcon } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { post } from "@/lib/api";
import Details, { DetailItem } from "@/components/ui/details";
import useDialog from "@/hooks/use-dialog";
import useTranslations from "@/hooks/use-translations";
import UpdatePaymentDetailsDialog from "./update-payment-details-dialog";

export default function AccountSheet({ open, onOpenChange }: SheetProps) {
  const currentPage = usePage();
  const { current_user } = currentPage.props;
  const { t } = useTranslations("shared.components.account_sheet");
  const billingDetails: DetailItem[] = [
    current_user?.subscription?.renews_at && {
      icon: CalendarIcon,
      label: t("next_billing_date"),
      value: formatDate(current_user.subscription.renews_at),
    },
    current_user?.subscription?.ends_at && {
      icon: CalendarIcon,
      label: t("end_date"),
      value: formatDate(current_user.subscription.ends_at),
    },
    {
      icon: CreditCardIcon,
      label: t("payment_method"),
      value: (
        <>
          {current_user?.subscription?.card_brand} {t("card_ending_in")}{" "}
          {current_user?.subscription?.card_last_four}
        </>
      ),
    },
  ].filter((item) => !!item);

  const updatePaymentDetailsDialog = useDialog();
  const accountForm = useForm({
    user: {
      email: current_user?.email,
      first_name: current_user?.first_name,
      last_name: current_user?.last_name,
    },
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn() {
      return post("billing/cancel");
    },
    onSettled() {
      router.reload();
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    accountForm.patch(account_path(), {
      onSuccess() {
        onOpenChange(false);
      },
    });
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full md:w-3/4">
          <SheetHeader>
            <SheetTitle>{t("title")}</SheetTitle>
          </SheetHeader>

          <form
            id="account-form"
            onSubmit={onSubmit}
            className="flex-1 flex flex-col gap-5 px-4 overflow-auto"
          >
            <FieldGroup>
              <FieldSet>
                <FieldLegend>{t("general_legend")}</FieldLegend>

                <Field>
                  <FieldLabel htmlFor="email">{t("email_label")}</FieldLabel>

                  <Input
                    type="email"
                    value={accountForm.data.user.email}
                    onChange={(event) =>
                      accountForm.setData("user.email", event.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="email">
                    {t("first_name_label")}
                  </FieldLabel>

                  <Input
                    type="first_name"
                    value={accountForm.data.user.first_name}
                    onChange={(event) =>
                      accountForm.setData("user.first_name", event.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="email">
                    {t("last_name_label")}
                  </FieldLabel>

                  <Input
                    type="last_name"
                    value={accountForm.data.user.last_name}
                    onChange={(event) =>
                      accountForm.setData("user.last_name", event.target.value)
                    }
                  />
                </Field>
              </FieldSet>

              <FieldSeparator />

              <FieldSet>
                <FieldLegend>{t("billing_legend")}</FieldLegend>

                {current_user?.subscription ? (
                  <>
                    <Details items={billingDetails} />
                    <FieldSeparator />

                    <div className="flex gap-2 items-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={updatePaymentDetailsDialog.open}
                        disabled={
                          current_user?.subscription.status === "canceled"
                        }
                      >
                        {t("update_payment")}
                      </Button>
                      {current_user?.subscription.status !== "canceled" && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="ml-auto text-destructive hover:text-destructive"
                          onClick={() => cancelSubscriptionMutation.mutate()}
                          disabled={cancelSubscriptionMutation.isPending}
                        >
                          {cancelSubscriptionMutation.isPending && <Spinner />}
                          {t("cancel_subscription")}
                        </Button>
                      )}
                    </div>
                  </>
                ) : null}
              </FieldSet>
            </FieldGroup>
          </form>

          <SheetFooter>
            <Button type="submit" form="account-form">
              {accountForm.processing && <Spinner />}
              {t("shared.actions.save")}
            </Button>
            <SheetClose asChild>
              <Button variant="outline" disabled={accountForm.processing}>
                {t("shared.actions.close")}
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      <UpdatePaymentDetailsDialog
        open={updatePaymentDetailsDialog.isOpen}
        onOpenChange={updatePaymentDetailsDialog.setIsOpen}
      />
    </>
  );
}
